import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { Users, Loader2, UserCircle, TrendingUp, Star, Filter, ArrowUpDown, ChevronDown, ListFilter, Check } from 'lucide-react';

export default function InstructorStudents() {
  const { currentUser } = useAuth();
  const [studentsList, setStudentsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'dateDesc', direction: 'desc' });
  const [courseFilter, setCourseFilter] = useState('all');
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [isCourseDropdownOpen, setIsCourseDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const coursesQ = query(collection(db, 'courses'), where('instructorId', '==', currentUser.uid));
        const coursesSnap = await getDocs(coursesQ);
        const myCourses = coursesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        let studentsListTemp = [];
        for (const course of myCourses) {
          const enrollmentsQ = query(collection(db, 'enrollments'), where('courseId', '==', course.id));
          const enrollmentsSnap = await getDocs(enrollmentsQ);

          for (const docSnap of enrollmentsSnap.docs) {
            const enrollData = docSnap.data();
            const studentDoc = doc(db, 'users', enrollData.userId);
            const studentSnap = await getDoc(studentDoc);
            if (studentSnap.exists()) {
              const sData = studentSnap.data();
              
              const completedCount = enrollData.completedLessons?.length || 0;
              const curriculum = course.curriculum || [];
              const totalLessonsCount = curriculum.reduce((acc, unit) => acc + (unit.lessons?.length || 0), 0) || course.totalLessons || 0;
              const progressPct = totalLessonsCount > 0 ? Math.min(100, Math.round((completedCount / totalLessonsCount) * 100)) : 0;
              const pointsEarned = enrollData.pointsEarned || enrollData.score || 0;

              studentsListTemp.push({
                enrollId: docSnap.id,
                rawDate: enrollData.enrolledAt ? enrollData.enrolledAt.toMillis() : 0,
                studentName: sData.name || sData.displayName || 'طالب مجهول',
                studentPhoto: sData.photoURL || '',
                courseTitle: course.title,
                enrollmentDate: enrollData.enrolledAt ? enrollData.enrolledAt.toDate().toLocaleDateString('ar-EG') : 'غير محدد',
                progress: progressPct,
                score: pointsEarned,
                actualPaidAmount: enrollData.actualPaidAmount || 0
              });
            }
          }
        }

        setStudentsList(studentsListTemp);
      } catch (err) {
        console.error("Error fetching students:", err);
      } finally {
        setLoading(false);
      }
    };
    if (currentUser) fetchStudents();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const filteredStudents = studentsList.filter(s => courseFilter === 'all' || s.courseTitle === courseFilter);

  const sortedStudents = [...filteredStudents].sort((a, b) => {
    switch (sortConfig.key) {
      case 'progressDesc': return b.progress - a.progress;
      case 'progressAsc': return a.progress - b.progress;
      case 'scoreDesc': return b.score - a.score;
      case 'scoreAsc': return a.score - b.score;
      case 'amountDesc': return b.actualPaidAmount - a.actualPaidAmount;
      case 'amountAsc': return a.actualPaidAmount - b.actualPaidAmount;
      case 'dateAsc': return a.rawDate - b.rawDate;
      case 'dateDesc':
      default: return b.rawDate - a.rawDate;
    }
  });

  const uniqueCourses = Array.from(new Set(studentsList.map(s => s.courseTitle)));
  const courseOptions = [
    { value: 'all', label: 'كل الدورات' },
    ...uniqueCourses.map(c => ({ value: c, label: c }))
  ];

  const sortOptions = [
    { value: 'dateDesc', label: 'الأحدث انضماماً' },
    { value: 'dateAsc', label: 'الأقدم انضماماً' },
    { value: 'progressDesc', label: 'الأعلى إنجازاً' },
    { value: 'progressAsc', label: 'الأقل إنجازاً' },
    { value: 'scoreDesc', label: 'الأعلى درجات' },
    { value: 'scoreAsc', label: 'الأقل درجات' },
    { value: 'amountDesc', label: 'الأكثر دفعاً' },
  ];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
          الطلاب المسجلين <span className="text-primary-600 dark:text-primary-400">({sortedStudents.length})</span>
        </h1>
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl overflow-hidden shadow-sm">
        
        {studentsList.length > 0 && (
          <div className="p-5 border-b border-slate-200/50 dark:border-white/5 bg-slate-50/50 dark:bg-black/20 flex flex-col md:flex-row gap-5 items-center justify-between">
            <div className="flex items-center gap-3 w-full md:w-auto">
              <div className="w-11 h-11 rounded-2xl bg-white dark:bg-zinc-800 flex items-center justify-center border border-slate-200 dark:border-white/10 shadow-sm shrink-0">
                <ListFilter className="w-5 h-5 text-primary-500" />
              </div>
              <div className="relative w-full md:w-64 shrink-0">
                <button
                  onClick={() => { setIsCourseDropdownOpen(!isCourseDropdownOpen); setIsSortDropdownOpen(false); }}
                  className="w-full flex items-center justify-between bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-zinc-300 font-bold rounded-xl px-4 py-2.5 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all shadow-sm"
                >
                  <span className="truncate">{courseOptions.find(o => o.value === courseFilter)?.label}</span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${isCourseDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isCourseDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setIsCourseDropdownOpen(false)} />
                    <div className="absolute top-full right-0 mt-2 w-full bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-2xl shadow-xl overflow-hidden z-40 animate-in fade-in zoom-in-95 duration-200">
                      <div className="max-h-60 overflow-y-auto p-2 space-y-1 custom-scrollbar relative">
                        {courseOptions.map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => {
                              setCourseFilter(opt.value);
                              setIsCourseDropdownOpen(false);
                            }}
                            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl font-bold transition-colors text-right relative overflow-hidden group ${courseFilter === opt.value ? 'bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400' : 'text-slate-600 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-white/5'}`}
                          >
                            <span className="truncate z-10">{opt.label}</span>
                            {courseFilter === opt.value && <Check className="w-4 h-4 shrink-0 z-10" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 w-full md:w-auto shrink-0">
              <div className="w-11 h-11 rounded-2xl bg-white dark:bg-zinc-800 flex items-center justify-center border border-slate-200 dark:border-white/10 shadow-sm shrink-0">
                <ArrowUpDown className="w-5 h-5 text-amber-500" />
              </div>
              <div className="relative w-full md:w-56 shrink-0">
                <button
                  onClick={() => { setIsSortDropdownOpen(!isSortDropdownOpen); setIsCourseDropdownOpen(false); }}
                  className="w-full flex items-center justify-between bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-zinc-300 font-bold rounded-xl px-4 py-2.5 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all shadow-sm"
                >
                  <span className="truncate">{sortOptions.find(o => o.value === sortConfig.key)?.label || 'ترتيب'}</span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${isSortDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isSortDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setIsSortDropdownOpen(false)} />
                    <div className="absolute top-full right-0 mt-2 w-full bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-2xl shadow-xl overflow-hidden z-40 animate-in fade-in zoom-in-95 duration-200">
                      <div className="max-h-60 overflow-y-auto p-2 space-y-1 custom-scrollbar relative">
                        {sortOptions.map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => {
                              setSortConfig({ key: opt.value, direction: opt.value.includes('Asc') ? 'asc' : 'desc' });
                              setIsSortDropdownOpen(false);
                            }}
                            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl font-bold transition-colors text-right relative overflow-hidden ${sortConfig.key === opt.value ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400' : 'text-slate-600 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-white/5'}`}
                          >
                            <span className="truncate z-10">{opt.label}</span>
                            {sortConfig.key === opt.value && <Check className="w-4 h-4 shrink-0 z-10" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {sortedStudents.length === 0 ? (
          <div className="p-16 text-center">
            <Users className="w-16 h-16 text-slate-300 dark:text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-500">لا يوجد بيانات لعرضها</h3>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-right border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-black/20 border-b border-slate-200 dark:border-white/10 text-slate-500 font-bold">
                  <th className="px-6 py-4">الطالب</th>
                  <th className="px-6 py-4">الدورة</th>
                  <th className="px-6 py-4">تاريخ الانضمام</th>
                  <th className="px-6 py-4 text-center">المبلغ المحصّل</th>
                  <th className="px-6 py-4 text-center">نسبة الإنجاز</th>
                  <th className="px-6 py-4 text-center">الدرجات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {sortedStudents.map((student, i) => {
                  const p = student.progress;
                  let pColor = "bg-slate-50 dark:bg-zinc-800/80 text-slate-600 dark:text-zinc-400";
                  if (p === 100) pColor = "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
                  else if (p >= 75) pColor = "bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400";
                  else if (p >= 50) pColor = "bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400";
                  else if (p > 0) pColor = "bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400";

                  return (
                    <tr key={i} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center overflow-hidden shrink-0">
                            {student.studentPhoto ? <img src={student.studentPhoto} alt={student.studentName} className="w-full h-full object-cover" /> : <UserCircle className="w-6 h-6 text-slate-400" />}
                          </div>
                          <span className="font-bold text-slate-900 dark:text-white">{student.studentName}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-600 dark:text-zinc-300 font-medium">{student.courseTitle}</td>
                      <td className="px-6 py-4 text-slate-500 text-sm">{student.enrollmentDate}</td>
                      <td className="px-6 py-4 text-center">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-full font-bold text-sm" dir="ltr">
                          ${student.actualPaidAmount.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-bold text-sm max-w-fit ${pColor}`}>
                          <TrendingUp className="w-4 h-4 shrink-0" />
                          {student.progress}%
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-full font-bold text-sm">
                          <Star className="w-4 h-4" />
                          {student.score}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
